import React, { useEffect, useMemo } from 'react';
import { Grid, RadioGroup, FormControlLabel, Radio, FormHelperText, FormControl } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormProps, getRequiredValidationRule, getValidationProps } from 'util/Form';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import { PreferenceQuestionOption, PreferenceQuestionView } from 'store/types/AuraAttribute';
import ExplanationQuestionFormItem from '../ExplanationQuestionFormItem';
interface RadioQuestionFormItemProps extends FormProps {
  question: PreferenceQuestionView;
}

const RadioQuestionFormItem: React.FunctionComponent<RadioQuestionFormItemProps> = ({ question, disabled }) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { fieldName, options = [], required = false } = question;
  const { error, helperText } = getValidationProps(fieldName, errors);
  const radioValue = useWatch({ control, name: fieldName });
  const optionExplanation: PreferenceQuestionOption | undefined = useMemo(
    () =>
      (options.filter(({ requiresDetail, id }) => !!requiresDetail && id === radioValue) ?? [])
        .map((option) => ({
          id: option.id,
          name: option.option,
          isSelected: option.selected ?? false,
          sequence: option.sequence,
          includeExplanation: option.requiresDetail,
        }))
        .at(0),
    [options, radioValue]
  );

  useEffect(() => {
    const selectedOption = options.find(({ selected }) => selected ?? false);

    if (selectedOption) {
      const { id } = selectedOption;

      setValue(fieldName, id, { shouldValidate: true });
    }
  }, [options, setValue, fieldName]);

  return (
    <Grid {...defaultGridContainerProps}>
      <Grid {...defaultGridItemProps}>
        <FormControl component={'fieldset'} fullWidth={true} required={required} error={error}>
          <Controller
            render={({ field }) => (
              <RadioGroup {...field} row={true}>
                {options.map(({ option: name, id }) => (
                  <FormControlLabel
                    key={`${fieldName}-option-${id}`}
                    value={id}
                    disabled={disabled}
                    control={<Radio color={'primary'} />}
                    label={name}
                  />
                ))}
              </RadioGroup>
            )}
            name={fieldName}
            control={control}
            rules={{
              required: getRequiredValidationRule('your answer', true, required),
            }}
            defaultValue={''}
          />
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      </Grid>
      {optionExplanation && (
        <Grid {...defaultGridItemProps}>
          <ExplanationQuestionFormItem
            disabled={disabled}
            parentFieldName={fieldName}
            required={required}
            defaultValue={optionExplanation.explanation}
          />
        </Grid>
      )}
    </Grid>
  );
};
export default RadioQuestionFormItem;
